// US-11 — /login rediseñada split-screen fintech (UC-11.08)
import { createFileRoute } from "@tanstack/react-router";
import { z } from "zod";
import { supabase } from "@/lib/supabase";
import { env } from "@/lib/env";
import { cn } from "@/lib/utils";

// Dos causas distintas, dos mensajes distintos. `not_whitelisted` es el rechazo
// real de la whitelist (hay sesión, el email no está autorizado); `session_failed`
// es que el inicio de sesión no llegó a completarse. Confundirlos costó unas dos
// horas de diagnóstico en el cutover del 2026-08-03: la pantalla acusaba a la
// cuenta mientras el problema estaba en el canje del token.
const loginSearchSchema = z.object({
  error: z.enum(["not_whitelisted", "session_failed"]).optional(),
});

function GoogleIcon() {
  return (
    <svg className="size-4" viewBox="0 0 24 24" aria-hidden="true">
      <path
        fill="#4285F4"
        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
      />
      <path
        fill="#34A853"
        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
      />
      <path
        fill="#FBBC05"
        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
      />
      <path
        fill="#EA4335"
        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z"
      />
    </svg>
  );
}

function LoginPage() {
  const { error } = Route.useSearch();

  async function handleGoogleSignIn() {
    await supabase.auth.signInWithOAuth({
      provider: "google",
      options: {
        // window.location.origin (no VITE_PUBLIC_BASE_URL): el callback debe
        // volver al host/puerto desde el que se abrió la app — en dev el puerto
        // puede cambiar (5180) y una env fija mandaría el login a otra app.
        redirectTo: `${window.location.origin}/auth/callback`,
      },
    });
  }

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-[3fr_2fr] bg-canvas">
      {/* Left — content */}
      <main className="flex flex-col items-start justify-center px-12 py-16 max-w-2xl mx-auto w-full">
        <div className="flex flex-col gap-2 mb-12 leading-none">
          <span className="text-2xl font-semibold tracking-tight text-fg">
            <span className="text-accent">H</span>Q
          </span>
          <span className="text-xs text-fg-tertiary uppercase tracking-[0.12em]">mIAsistente</span>
        </div>

        <h1 className="text-2xl font-semibold tracking-tight text-fg mb-3">
          Control center personal
        </h1>
        <p className="text-md text-fg-secondary max-w-md mb-10">
          Tu facturación, agenda, email unificado y actividad deportiva en una pantalla.
          Single-tenant, single-account, single-owner.
        </p>

        {error && (
          <div
            role="alert"
            className={cn(
              "mb-6 px-4 py-3 rounded-md max-w-md w-full",
              "bg-danger-soft border border-danger/30 text-danger text-sm",
            )}
          >
            {error === "not_whitelisted"
              ? "Acceso restringido. Esta cuenta no está autorizada."
              : "No se pudo completar el inicio de sesión. Vuelve a intentarlo."}
          </div>
        )}

        <button
          type="button"
          onClick={handleGoogleSignIn}
          className={cn(
            "inline-flex items-center justify-center gap-3 h-10 px-5 rounded-md max-w-md w-full",
            "bg-accent hover:bg-accent-strong text-white",
            "text-sm font-medium transition-colors duration-fast",
            "focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/40",
          )}
        >
          <GoogleIcon />
          Continuar con Google
        </button>

        <p className="mt-4 text-xs text-fg-tertiary">
          Email autorizado: <span className="font-mono tabular text-fg-secondary">{env.VITE_ALLOWED_EMAIL}</span>
        </p>

        <div className="mt-auto pt-12 flex items-center gap-3">
          <span
            className={cn(
              "inline-flex items-center px-2 py-0.5 rounded-sm",
              "font-mono text-xs uppercase tracking-wide tabular",
              "bg-subtle text-fg-tertiary border border-default",
            )}
          >
            v0.3.1 · Fase 6
          </span>
          <span className="text-xs text-fg-tertiary">
            Single-tenant · jesus.perez.developer@gmail.com only
          </span>
        </div>
      </main>

      {/* Right — gradient panel (única excepción gradient permitida en US-11) */}
      <aside
        className="hidden lg:flex relative overflow-hidden"
        style={{
          background:
            "linear-gradient(135deg, var(--accent-soft) 0%, var(--bg-canvas) 60%, var(--bg-subtle) 100%)",
        }}
        aria-hidden="true"
      >
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="relative">
            <div className="absolute -inset-32 rounded-full bg-accent/8 blur-3xl" />
            <span className="relative text-[180px] font-semibold tabular leading-none text-fg/5 tracking-tight">
              HQ
            </span>
          </div>
        </div>
      </aside>
    </div>
  );
}

export const Route = createFileRoute("/login")({
  validateSearch: loginSearchSchema,
  component: LoginPage,
});
